import { positionsAtDate } from '../../astro/ephemeris.ts';
import { AYANAMSA } from '../../astro/ayanamsa.ts';
import { NAKSHATRAS, RASHI_ENGLISH, placementOf, type Placement } from '../../astro/zodiac.ts';
import { confident, precisionToMinutes } from '../../astro/uncertainty.ts';
import { resolveBirthTime } from '../../astro/time.ts';
import { matchKundli, type MatchResult } from '../../astro/kundli.ts';
import { vashyaClassOf } from '../../astro/tables/ashtakoot.ts';
import { readBirthDetails, renderTimeProblem, type BirthDetails } from './birth-details.ts';
import { reportToolComplete, reportToolCta, reportToolStart } from './analytics.ts';

/**
 * Kundli Matching (Ashtakoot) — client driver.
 *
 * The two decided product rules (HANDOFF.md §9) shape everything here:
 *   1. No marry / don't-marry verdict. The result is the score, what each
 *      kuta measures, and the working — never a judgement.
 *   2. Doshas are reported, never auto-cancelled.
 *
 * And the house uncertainty discipline applies with extra force: a kuta
 * score people make marriage decisions on must never be computed from a
 * Moon position the stated birth-time precision cannot actually pin down.
 * The Moon moves ~0.5° per hour; a vague time can move it across a
 * nakshatra, sign, or Vashya half-sign boundary. Each of those three
 * projections is gated independently, per person, and a failed gate stops
 * the match with an explanation — not a guess.
 */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

type MoonReading =
  | { ok: true; placement: Placement }
  | { ok: false; problem: HTMLElement };

/** Resolves one person's Moon placement, or explains why it can't be pinned. */
function readMoon(details: BirthDetails, who: string): MoonReading {
  if (details.precision === 'unknown') {
    const box = el('div', 'tool-caveat tool-caveat--block');
    box.appendChild(el('h4', 'tool-caveat__title', `${who}: we need at least a rough birth time`));
    box.appendChild(el('p', undefined,
      'The Moon moves about half a degree every hour, and all eight kutas are read from the ' +
      'Moon. Across a whole day it can cross a nakshatra and even a sign, so a match computed ' +
      'from "time unknown" would silently pick one of two different results. Even "within a ' +
      'few hours" is usually enough for us to check whether the answer is stable.'));
    return { ok: false, problem: box };
  }

  const resolved = resolveBirthTime(details.wall, details.timezone, details.longitude);
  if (!resolved.ok) return { ok: false, problem: renderTimeProblem(resolved) };

  const utc = resolved.utc;
  const place = { latitude: details.latitude, longitude: details.longitude };
  const deltaMinutes = precisionToMinutes(details.precision);
  const moonLon = (t: Date) => positionsAtDate(t, place).longitudes.Moon;

  /* Three independent gates, strictest honest set the match needs:
     nakshatra (Tara/Yoni/Gana/Nadi), sign (Varna/Maitri/Bhakoot), and the
     Vashya class — which can flip mid-sign in Sagittarius and Capricorn. */
  const nakGate = confident((t) => placementOf(moonLon(t)).nakshatraIndex, utc, deltaMinutes, (v) => v);
  const signGate = confident((t) => placementOf(moonLon(t)).rashiIndex, utc, deltaMinutes, (v) => v);
  const vashyaGate = confident((t) => {
    const p = placementOf(moonLon(t));
    return vashyaClassOf(p.rashiIndex, p.degreeInRashi);
  }, utc, deltaMinutes);

  if (!nakGate.certain || !signGate.certain || !vashyaGate.certain) {
    const box = el('div', 'tool-caveat tool-caveat--block');
    box.appendChild(el('h4', 'tool-caveat__title', `${who}: the birth time can't settle the Moon`));
    if (!nakGate.certain) {
      box.appendChild(el('p', undefined,
        `Within the time range you gave, the Moon crosses from ${NAKSHATRAS[nakGate.candidates[0]!]} ` +
        `into ${NAKSHATRAS[nakGate.candidates[nakGate.candidates.length - 1]!]}. Four of the eight ` +
        'kutas are read from the nakshatra, so the whole match would change with it.'));
    } else if (!signGate.certain) {
      box.appendChild(el('p', undefined,
        `Within the time range you gave, the Moon crosses from ${RASHI_ENGLISH[signGate.candidates[0]!]} ` +
        `into ${RASHI_ENGLISH[signGate.candidates[signGate.candidates.length - 1]!]}. Varna, Graha ` +
        'Maitri and Bhakoot are all read from the Moon sign.'));
    } else {
      box.appendChild(el('p', undefined,
        'Within the time range you gave, the Moon crosses the half-sign boundary that decides ' +
        'the Vashya group. Everything else is stable, but we would rather pause on one kuta ' +
        'than quietly pick a side.'));
    }
    box.appendChild(el('p', undefined,
      'A birth certificate time — or even narrowing to the nearest half hour — usually resolves this.'));
    return { ok: false, problem: box };
  }

  return { ok: true, placement: placementOf(moonLon(utc)) };
}

function scoreBar(score: number, max: number): HTMLElement {
  const bar = el('div', 'kuta-bar');
  bar.setAttribute('role', 'img');
  bar.setAttribute('aria-label', `${score} of ${max}`);
  const fill = el('span', 'kuta-bar__fill');
  fill.style.width = `${(score / max) * 100}%`;
  bar.appendChild(fill);
  return bar;
}

function renderResult(
  output: HTMLElement,
  result: MatchResult,
  bride: { details: BirthDetails; placement: Placement },
  groom: { details: BirthDetails; placement: Placement },
): void {
  // ── Head: who × who, each Moon stated plainly ─────────────────────────
  const head = el('div', 'kundli-result__head');
  head.appendChild(el('p', 'tool-result__where',
    `${bride.details.name || 'Bride'} × ${groom.details.name || 'Groom'}`));
  const moons = el('div', 'kundli-result__moons');
  for (const [label, side] of [['Bride', bride], ['Groom', groom]] as const) {
    const box = el('div', 'kundli-result__moon');
    box.appendChild(el('span', 'kundli-result__moon-label', label));
    box.appendChild(el('strong', undefined,
      `${side.placement.nakshatra} · ${side.placement.rashiEnglish}`));
    box.appendChild(el('span', 'kundli-result__moon-sub',
      `Moon, pada ${side.placement.pada}`));
    moons.appendChild(box);
  }
  head.appendChild(moons);
  output.appendChild(head);

  // ── Total — a tabulation, never a judgement ───────────────────────────
  const totalWrap = el('div', 'kundli-total');
  const totalNum = el('p', 'kundli-total__num');
  totalNum.appendChild(el('strong', undefined, String(result.total)));
  totalNum.appendChild(el('span', undefined, ` / ${result.max}`));
  totalWrap.appendChild(totalNum);
  totalWrap.appendChild(el('p', 'kundli-total__label', 'Ashtakoot guna total'));
  totalWrap.appendChild(el('p', 'kundli-total__note',
    'This total is a classical tabulation, not a judgement. What a number means for two ' +
    'real people — and what the doshas below actually ask of a match — is a reading, ' +
    'not arithmetic.'));
  output.appendChild(totalWrap);

  // ── The eight kutas, with the working shown ───────────────────────────
  const list = el('div', 'kuta-list');
  for (const k of result.kutas) {
    const row = el('details', 'kuta-row');
    const summary = el('summary', 'kuta-row__summary');
    const name = el('span', 'kuta-row__name');
    name.appendChild(el('strong', undefined, k.name));
    name.appendChild(el('span', 'kuta-row__measures', k.measures));
    summary.appendChild(name);
    const scoreBox = el('span', 'kuta-row__score');
    scoreBox.appendChild(el('strong', undefined, String(k.score)));
    scoreBox.appendChild(el('span', undefined, `/${k.max}`));
    summary.appendChild(scoreBox);
    summary.appendChild(scoreBar(k.score, k.max));
    row.appendChild(summary);
    const body = el('div', 'kuta-row__body');
    body.appendChild(el('p', undefined, k.working));
    if (k.disputed) body.appendChild(el('p', 'kuta-row__disputed', k.disputed));
    row.appendChild(body);
    list.appendChild(row);
  }
  output.appendChild(list);

  // ── Doshas: reported, never auto-cancelled ────────────────────────────
  const present = result.doshas.filter((d) => d.present);
  if (present.length > 0) {
    const doshas = el('div', 'kundli-doshas');
    doshas.appendChild(el('h4', 'tool-result__subhead', 'Doshas present'));
    for (const d of present) {
      const item = el('div', 'kundli-dosha');
      item.appendChild(el('strong', undefined, d.name));
      item.appendChild(el('p', undefined, d.note));
      doshas.appendChild(item);
    }
    output.appendChild(doshas);
  }

  // ── Method disclosure ─────────────────────────────────────────────────
  const method = el('details', 'tool-method');
  method.appendChild(el('summary', undefined, 'How this match is calculated'));
  method.appendChild(el('p', undefined,
    `${result.convention} Moon positions are sidereal (${AYANAMSA.label} ayanamsa), computed ` +
    'in your browser for the exact birth moments given. Anything the stated birth-time ' +
    'precision cannot settle stops the match rather than being guessed.'));
  output.appendChild(method);

  // ── Bridge: the one thing arithmetic can't do ─────────────────────────
  const bookUrl = output.getAttribute('data-book-url');
  if (bookUrl) {
    const bridge = el('div', 'tool-bridge');
    bridge.appendChild(el('p', 'tool-bridge__text',
      present.length > 0
        ? 'A dosha is a question, not a sentence — tradition itself disagrees about when each ' +
          'one is cancelled. A reading looks at both full charts, not just the Moons, before ' +
          'saying anything.'
        : 'A strong total is a good start — and still only the Moons talking. A reading ' +
          'weighs both full charts, the dashas, and what you are actually planning.'));
    const cta = el('a', 'btn btn-primary', 'Book a free 30-min call');
    cta.setAttribute('href', bookUrl);
    cta.setAttribute('target', '_blank');
    cta.setAttribute('rel', 'noopener');
    cta.addEventListener('click', () => {
      reportToolCta('kundli-matching', present.length > 0 ? 'dosha-present' : 'clear');
    });
    bridge.appendChild(cta);
    output.appendChild(bridge);
  }
}

export function initKundliTool(): void {
  const brideForm = document.getElementById('kundli-form-bride') as HTMLFormElement | null;
  const groomForm = document.getElementById('kundli-form-groom') as HTMLFormElement | null;
  const compute = document.getElementById('kundli-compute') as HTMLButtonElement | null;
  const output = document.getElementById('kundli-output');
  const skeleton = document.getElementById('kundli-skeleton');
  if (!brideForm || !groomForm || !compute || !output || !skeleton) return;

  let started = false;
  compute.addEventListener('click', () => {
    const bride = readBirthDetails(brideForm);
    const groom = bride ? readBirthDetails(groomForm) : null;
    if (!bride || !groom) return;
    if (!started) { started = true; reportToolStart('kundli-matching'); }

    output.hidden = true;
    output.classList.remove('is-revealed');
    skeleton.hidden = false;

    window.setTimeout(() => {
      skeleton.hidden = true;
      output.replaceChildren();

      const brideMoon = readMoon(bride, bride.name || 'Bride');
      const groomMoon = readMoon(groom, groom.name || 'Groom');

      if (!brideMoon.ok || !groomMoon.ok) {
        if (!brideMoon.ok) output.appendChild(brideMoon.problem);
        if (!groomMoon.ok) output.appendChild(groomMoon.problem);
        output.hidden = false;
        requestAnimationFrame(() => output.classList.add('is-revealed'));
        return; // an explanation is not a completion — don't report one
      }

      const result = matchKundli(brideMoon.placement, groomMoon.placement);
      renderResult(output, result,
        { details: bride, placement: brideMoon.placement },
        { details: groom, placement: groomMoon.placement });

      output.hidden = false;
      requestAnimationFrame(() => output.classList.add('is-revealed'));
      reportToolComplete('kundli-matching');
    }, 500);
  });
}
